/**
 * BundleCard - Card component for displaying an NFT bundle preview
 *
 * Shows bundle price, NFT count, and preview thumbnails.
 * Clicking opens the bundle detail modal.
 */

import React from 'react';
import { Avatar } from '../../ui/components/Avatar';
import { Badge } from '../../ui/components/Badge';
import { formatPrice } from '../../utils/formatters';
import { is3DModelUrl } from '../../utils/media';
import type { NFTBundle, BundleItem } from '../../api/bundles';

interface BundleCardProps {
  bundle: NFTBundle;
  onClick: () => void;
  showSeller?: boolean;
}

export const BundleCard: React.FC<BundleCardProps> = ({
  bundle,
  onClick,
  showSeller = false,
}) => {
  const itemCount = bundle.itemCount || bundle.items?.length || 0;
  const previewItems = bundle.items?.slice(0, 4) || [];

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col gap-3 p-4 rounded-xl border border-neutral-border bg-default-background hover:border-brand-300 hover:shadow-md transition-all text-left w-full"
    >
      {/* Preview thumbnails grid */}
      <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-neutral-50">
        {previewItems.length > 0 ? (
          <div className={`grid h-full w-full ${
            previewItems.length === 1 ? 'grid-cols-1' :
            previewItems.length === 2 ? 'grid-cols-2' :
            'grid-cols-2'
          } gap-0.5`}>
            {previewItems.map((item, index) => (
              <div
                key={item.nftTokenId}
                className={`relative ${
                  previewItems.length === 3 && index === 0 ? 'row-span-2' : ''
                }`}
              >
                {is3DModelUrl(item.nft_thumbnail_url || item.nft_image_url) ? (
                  <div className="w-full h-full bg-gradient-to-br from-neutral-100 to-neutral-200 flex flex-col items-center justify-center">
                    <span className="text-lg">🧊</span>
                  </div>
                ) : (
                  <img
                    src={item.nft_thumbnail_url || item.nft_image_url || '/placeholder-nft.png'}
                    alt={item.nft_name || 'NFT'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/placeholder-nft.png';
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <span className="text-subtext-color">No preview</span>
          </div>
        )}

        {/* Item count badge */}
        <div className="absolute bottom-2 right-2">
          <Badge variant="neutral" className="bg-black/70 text-white border-0">
            {itemCount} NFT{itemCount !== 1 ? 's' : ''}
          </Badge>
        </div>
      </div>

      {/* Bundle info */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className="text-body-bold text-default-font">
            Bundle
          </span>
          <span className="text-body-bold text-brand-600">
            {formatPrice(bundle.priceXrp)}
          </span>
        </div>

        <div className="flex items-center justify-between text-caption text-subtext-color">
          <span>
            {itemCount} item{itemCount !== 1 ? 's' : ''}
            {itemCount > 0 && (
              <span className="ml-1 text-neutral-400">
                ({formatPrice(bundle.priceXrp / itemCount)} each)
              </span>
            )}
          </span>
        </div>

        {showSeller && bundle.sellerAddress && (
          <div className="flex items-center gap-2 mt-1">
            <span className="text-caption text-subtext-color">
              Seller: {bundle.sellerAddress.slice(0, 6)}...{bundle.sellerAddress.slice(-4)}
            </span>
          </div>
        )}
      </div>
    </button>
  );
};

export default BundleCard;
